// apps/web/src/features/commercial-proposals/queries.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CommercialProposalDetailResponse,
  CommercialProposalListResponse,
  CommercialProposalViewsResponse,
  CreateCommercialProposalInput,
  PatchCommercialProposalInput,
} from '@magimp/shared';

import {
  createCommercialProposal,
  getCommercialProposal,
  getCommercialProposalViews,
  listCommercialProposals,
  patchCommercialProposal,
  publishCommercialProposal,
  revokeCommercialProposal,
} from '../../api/commercial-proposals.js';

/**
 * @aiPurpose React Query key factory + CRUD hooks for the commercial-
 *   proposals workspace (#1198).
 * @aiInvariant every mutation `onSuccess` invalidates BOTH the list prefix
 *   `['commercial-proposals']` and the affected detail
 *   `['commercial-proposal', id]` so any list view stays consistent
 *   regardless of which slice is currently cached.
 */
export const commercialProposalsKeys = {
  all: ['commercial-proposals'] as const,
  list: () => ['commercial-proposals', 'list'] as const,
  detail: (id: string) => ['commercial-proposal', id] as const,
  views: (id: string) => ['commercial-proposal', id, 'views'] as const,
};

// -- queries ----------------------------------------------------------

export function useCommercialProposalsList(options: { enabled?: boolean } = {}) {
  return useQuery<CommercialProposalListResponse>({
    queryKey: commercialProposalsKeys.list(),
    queryFn: ({ signal }) => listCommercialProposals({ signal }),
    enabled: options.enabled ?? true,
  });
}

export function useCommercialProposalDetail(
  id: string | null,
  options: { enabled?: boolean } = {},
) {
  return useQuery<CommercialProposalDetailResponse>({
    queryKey: id ? commercialProposalsKeys.detail(id) : ['commercial-proposal', '__none__'],
    queryFn: ({ signal }) => {
      if (!id) throw new Error('useCommercialProposalDetail called without id');
      return getCommercialProposal(id, { signal });
    },
    enabled: options.enabled !== false && id !== null,
  });
}

export function useCommercialProposalViews(id: string | null, options: { enabled?: boolean } = {}) {
  return useQuery<CommercialProposalViewsResponse>({
    queryKey: id ? commercialProposalsKeys.views(id) : ['commercial-proposal', '__none__', 'views'],
    queryFn: ({ signal }) => {
      if (!id) throw new Error('useCommercialProposalViews called without id');
      return getCommercialProposalViews(id, { signal });
    },
    enabled: options.enabled !== false && id !== null,
  });
}

// -- mutations --------------------------------------------------------

function useInvalidate() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: commercialProposalsKeys.all });
    if (id) void qc.invalidateQueries({ queryKey: commercialProposalsKeys.detail(id) });
  };
}

export function useCreateCommercialProposal() {
  const invalidate = useInvalidate();
  return useMutation<CommercialProposalDetailResponse, Error, CreateCommercialProposalInput>({
    mutationFn: (input) => createCommercialProposal(input),
    onSuccess: (resp) => invalidate(resp.proposal.id),
  });
}

export function usePatchCommercialProposal(id: string) {
  const invalidate = useInvalidate();
  return useMutation<CommercialProposalDetailResponse, Error, PatchCommercialProposalInput>({
    mutationFn: (patch) => patchCommercialProposal(id, patch),
    onSuccess: () => invalidate(id),
  });
}

export function usePublishCommercialProposal(id: string) {
  const invalidate = useInvalidate();
  return useMutation<CommercialProposalDetailResponse, Error, void>({
    mutationFn: () => publishCommercialProposal(id),
    onSuccess: () => invalidate(id),
  });
}

export function useRevokeCommercialProposal(id: string) {
  const invalidate = useInvalidate();
  return useMutation<CommercialProposalDetailResponse, Error, void>({
    mutationFn: () => revokeCommercialProposal(id),
    onSuccess: () => invalidate(id),
  });
}
